import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, ShieldCheck, ArrowLeft } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import Button from '@components/common/Button';
import Input from '@components/common/Input';

const loginSchema = z.object({
  email: z.string().email('Email invalido'),
  password: z.string().min(6, 'Senha deve ter no minimo 6 caracteres'),
});

const Login = () => {
  const { login, loginWith2FA } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [twoFAStep, setTwoFAStep] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const result = await login(data.email, data.password);
      if (result && result.requires2FA) {
        setTempToken(result.tempToken);
        setTwoFAStep(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value.length > 1) {
      // Paste handling - distribute digits across inputs
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newCode = [...twoFACode];
      digits.forEach((d, i) => {
        if (index + i < 6) newCode[index + i] = d;
      });
      setTwoFACode(newCode);
      const nextIndex = Math.min(index + digits.length, 5);
      inputRefs.current[nextIndex]?.focus();
      return;
    }

    const newCode = [...twoFACode];
    newCode[index] = value.replace(/\D/g, '');
    setTwoFACode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !twoFACode[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify2FA = async () => {
    const code = twoFACode.join('');
    if (code.length < 6) return;
    setIsLoading(true);
    try {
      await loginWith2FA(tempToken, code);
    } finally {
      setIsLoading(false);
    }
  };

  if (twoFAStep) {
    const code = twoFACode.join('');
    return (
      <div>
        <button
          onClick={() => { setTwoFAStep(false); setTwoFACode(['', '', '', '', '', '']); }}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-6"
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-2xl flex items-center justify-center">
            <ShieldCheck size={32} className="text-primary-600" />
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
          Verificacao em Duas Etapas
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-8 text-center">
          Digite o codigo do seu aplicativo autenticador ou um codigo de backup
        </p>

        <div className="flex justify-center gap-2 mb-6">
          {twoFACode.map((digit, i) => (
            <input
              key={i}
              ref={el => inputRefs.current[i] = el}
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={digit}
              onChange={e => handleCodeChange(i, e.target.value)}
              onKeyDown={e => handleCodeKeyDown(i, e)}
              className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition-colors"
              autoFocus={i === 0}
            />
          ))}
        </div>

        <Button
          fullWidth
          isLoading={isLoading}
          onClick={handleVerify2FA}
          disabled={code.length < 6}
        >
          Verificar
        </Button>

        <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          Perdeu acesso ao autenticador? Use um codigo de backup digitando-o no campo acima.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Bem-vindo de volta
      </h2>
      <p className="text-gray-600 dark:text-gray-400 mb-8">
        Entre com sua conta para continuar
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Email"
          type="email"
          placeholder="seu@email.com"
          leftIcon={<Mail size={18} />}
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="Senha"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          leftIcon={<Lock size={18} />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="focus:outline-none"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">Lembrar-me</span>
          </label>
          <Link
            to="/forgot-password"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Esqueceu a senha?
          </Link>
        </div>

        <Button type="submit" fullWidth isLoading={isLoading}>
          Entrar
        </Button>
      </form>


      <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
        Nao tem uma conta?{' '}
        <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
          Criar conta gratis
        </Link>
      </p>
    </div>
  );
};

export default Login;
